import api from "./api";
import { ICarWashObject } from "../types/carWash";
import { IGetChangeReviewsRes, IChangeReview } from "../types/review";
import { IEligibleRM } from "./api.types";
import { AxiosResponse } from "axios";
import i18n from "../i18n";

interface ICreateCarWashRes {
  status: string;
  data: ICarWashObject;
}

interface IGetCarWashesRes {
  status: string;
  results: number;
  data: ICarWashObject[];
}

const getCarWashInfo = async (_id: string): Promise<ICarWashObject> => {
  try {
    const response = await api.get(`/admins/carWashes/${_id}`);
    return response.data.data; // Return the car wash data directly
  } catch (error: any) {
    if (error.response && error.response.data) {
      throw new Error(
        error.response.data.message || i18n.t("serviceErrors.fetchCarWash")
      );
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
};

// Fetch a list of car washes (could be used in a dashboard)
const getCarWashes = async (): Promise<IGetCarWashesRes> => {
  try {
    const response: AxiosResponse<IGetCarWashesRes> =
      await api.get<IGetCarWashesRes>("/admins/carWashes");
    return response.data; // Return the car wash data directly
  } catch (error: any) {
    if (error.response && error.response.data) {
      throw new Error(
        error.response.data.message || i18n.t("serviceErrors.fetchCarWashes")
      );
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
};

// Create a new car wash (supports FormData for file uploads)
const createCarWash = async (
  carWashData: FormData
): Promise<ICreateCarWashRes> => {
  try {
    const response: AxiosResponse<ICreateCarWashRes> =
      await api.post<ICreateCarWashRes>("/admins/carWashes", carWashData, {
        headers: {
          "Content-Type": "multipart/form-data", // Needed for file uploads
        },
      });
    return response.data; // Return the created car wash data
  } catch (error: any) {
    if (error.response && error.response.data) {
      throw new Error(
        error.response.data.message || i18n.t("serviceErrors.createCarWash")
      );
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
};

// Update an existing car wash
const updateCarWash = async (
  _id: string,
  carWashData: Partial<ICarWashObject>
): Promise<any> => {
  try {
    const response = await api.patch(`/admins/carWashes/${_id}`, carWashData);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      throw new Error(
        error.response.data.message || i18n.t("serviceErrors.updateCarWash")
      );
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
};

// Delete a car wash
const deleteCarWash = async (_id: string): Promise<any> => {
  try {
    const response = await api.delete(`/admins/carWashes/${_id}`);
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      throw new Error(
        error.response.data.message || i18n.t("serviceErrors.deleteCarWash")
      );
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
};

// Approve a review for a car wash
const processReview = async (
  carWashId: string,
  reviewId: string,
  status: "approved" | "rejected",
  reviewComment?: string
): Promise<any> => {
  try {
    const response = await api.patch(
      `/admins/carWashes/${carWashId}/reviews/${reviewId}/processReview`,
      {
        status: status,
        reviewComment: reviewComment || "",
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      throw new Error(
        error.response.data.message || i18n.t("serviceErrors.approveReview")
      );
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
};

// Reject a review for a car wash
const rejectReview = async (
  carWashId: string,
  reviewId: string
): Promise<any> => {
  try {
    const response = await api.patch(
      `/admins/carWashes/${carWashId}/reviews/${reviewId}/reject`
    );
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || i18n.t("serviceErrors.rejectReview"));
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
};

// Get all reviews for a car wash
const getCarWashReviews = async (
  carWashId: string
): Promise<IGetChangeReviewsRes> => {
  try {
    const response: AxiosResponse<IGetChangeReviewsRes> =
      await api.get<IGetChangeReviewsRes>(
        `/admins/carWashes/${carWashId}/reviews`
      );
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.message || i18n.t("serviceErrors.fetchReviews"));
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
};

// ---------------------------------------------------------------------------
// Phase 28 — RM-scoped read methods.
// These endpoints use the ApiResponse.success envelope `{ status, message, data }`
// (unlike the legacy getCarWashes which returns a raw `{ status, results, data }`),
// so getMyChangeReviews reads `response.data.data` (RESEARCH Pitfall 5).
// ---------------------------------------------------------------------------

// RM "Pending Review" tab — pending change requests scoped to the current RM.
const getMyChangeReviews = async (): Promise<IChangeReview[]> => {
  try {
    const response = await api.get("/admins/rm/changeReviews");
    return response.data.data; // ApiResponse.success envelope — NOT response.data
  } catch (error: any) {
    if (error.response && error.response.data) {
      throw new Error(
        error.response.data.message || i18n.t("serviceErrors.fetchMyPendingReviews")
      );
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
};

// Super-admin — all pending carWash ChangeReviews across all owners (Phase 30-01),
// including create-type reviews where `carWash` is null (no CarWash doc exists yet).
const getAllPendingCarWashReviews = async (): Promise<IChangeReview[]> => {
  try {
    const response = await api.get("/admins/changeReviews");
    return response.data.data; // ApiResponse.success envelope — NOT response.data
  } catch (error: any) {
    if (error.response && error.response.data) {
      throw new Error(
        error.response.data.message || i18n.t("serviceErrors.fetchPendingCarWashReviews")
      );
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
};

// Super-admin — process (approve/reject) a pending carWash ChangeReview by reviewId
// only. Unlike `processReview`, this does NOT require a carWashId, so it works for
// create-type reviews (`carWash: null`) as well as update-type reviews.
const processCarWashReview = async (
  reviewId: string,
  status: "approved" | "rejected",
  reviewComment?: string
): Promise<any> => {
  try {
    const response = await api.patch(
      `/admins/changeReviews/${reviewId}/processReview`,
      {
        status,
        reviewComment: reviewComment || "",
      }
    );
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      throw new Error(
        error.response.data.message ||
          (status === "approved"
            ? i18n.t("serviceErrors.acceptChangeRequest")
            : i18n.t("serviceErrors.rejectChangeRequest"))
      );
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
};

// Super-admin — Assign RM picker (260801-sa1). Server pre-filters to
// relationship_manager-role admins only (defense in depth also enforced
// server-side on assignOwnerRM below).
const getEligibleRMs = async (): Promise<IEligibleRM[]> => {
  try {
    const response = await api.get("/admins/rms");
    return response.data.data; // ApiResponse.success envelope — NOT response.data
  } catch (error: any) {
    if (error.response && error.response.data) {
      throw new Error(
        error.response.data.message || i18n.t("serviceErrors.fetchEligibleRMs")
      );
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
};

// Super-admin — manually assign an owner's reviewing RM (260801-sa1 D-01/D-02).
const assignOwnerRM = async (ownerId: string, rmId: string): Promise<any> => {
  try {
    const response = await api.patch(`/admins/owners/${ownerId}/assignRM`, {
      rmId,
    });
    return response.data;
  } catch (error: any) {
    if (error.response && error.response.data) {
      throw new Error(
        error.response.data.message || i18n.t("serviceErrors.assignRM")
      );
    } else {
      throw new Error(i18n.t("serviceErrors.serverError"));
    }
  }
};

export {
  getCarWashes,
  createCarWash,
  getCarWashInfo,
  updateCarWash,
  deleteCarWash,
  processReview,
  rejectReview,
  getCarWashReviews,
  getMyChangeReviews,
  getAllPendingCarWashReviews,
  processCarWashReview,
  getEligibleRMs,
  assignOwnerRM,
};
